import { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Add, Close, DeleteOutline, Edit, Save } from '@mui/icons-material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../../components/shared/ConfirmDialog';
import type { RockTCSReadingRow } from '../../../../types/rockTcs';
import { saveRockTcsReadings, type RockTCSReadingEdit } from '../../../../services/rockTcsService';
import { TCS_TABLE_COLUMNS } from '../rockTcsFields';
import { fmt } from '../sectionStyles';
import { CombinedDataTable } from './CombinedDataTable';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import { useRowRangeSelection } from '../../../../hooks/useRowRangeSelection';
import { ROW_RANGE_SELECT_TIP, SELECT_ALL_ROWS_TOOLTIP } from '../../../../constants/rowSelection';
import { FormulaHeaderTooltip } from '../../components/FormulaHeaderTooltip';

interface Props {
  workOrderDetailKey: string;
  readings: RockTCSReadingRow[];
  maxRowIndex: number | null;
  fiftyRowIndex: number | null;
  readOnly?: boolean;
  /** Reload readings + general (peak results) after a successful save. */
  onSaved: () => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

/** The five raw machine inputs — the only editable columns; the rest are derived. */
const EDITABLE_FIELDS = new Set(
  TCS_TABLE_COLUMNS.filter((c) => c.group === 'Imported').map((c) => c.field),
);

interface DraftRow {
  _cid: string;
  original: RockTCSReadingRow | null; // null → newly added row
  timeS: string;
  frameLoadKN: string;
  axialDisp1Mm: string;
  axialDisp2Mm: string;
  radialStrainPct: string;
}

const toStr = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
const toNum = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export const ReadingsDataPanel = ({
  workOrderDetailKey, readings, maxRowIndex, fiftyRowIndex, readOnly, onSaved, onNotify,
}: Props) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  const sel = useRowRangeSelection();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const cidRef = useRef(0);
  const nextCid = () => `r${cidRef.current++}`;
  const { formulaFor } = useTestFormulas('rockTcs');

  const groups = useMemo(() => {
    const seen: { label: string; span: number }[] = [];
    for (const col of TCS_TABLE_COLUMNS) {
      const last = seen[seen.length - 1];
      if (last && last.label === col.group) last.span += 1;
      else seen.push({ label: col.group, span: 1 });
    }
    return seen;
  }, []);

  const enterEdit = () => {
    setDraft(readings.map((r) => ({
      _cid: nextCid(),
      original: r,
      timeS: toStr(r.timeS),
      frameLoadKN: toStr(r.frameLoadKN),
      axialDisp1Mm: toStr(r.axialDisp1Mm),
      axialDisp2Mm: toStr(r.axialDisp2Mm),
      radialStrainPct: toStr(r.radialStrainPct),
    })));
    setPage(0);
    setDirty(false);
    sel.clear();
    setEditing(true);
  };

  const cancelEdit = () => {
    const close = () => { setEditing(false); setDirty(false); setDraft([]); sel.clear(); };
    if (dirty) {
      setConfirmDlg({
        open: true, title: 'Discard changes?',
        message: 'You have unsaved edits to the test data. Discard them?',
        confirmLabel: 'Discard', confirmColor: 'warning', onConfirm: close,
      });
    } else close();
  };

  const setCell = (cid: string, field: keyof DraftRow, value: string) => {
    setDraft((prev) => prev.map((row) => (row._cid === cid ? { ...row, [field]: value } : row)));
    setDirty(true);
  };

  const deleteRow = (cid: string) => {
    setDraft((prev) => prev.filter((row) => row._cid !== cid));
    sel.removeId(cid);
    setDirty(true);
  };

  // ── Bulk selection (single-toggle, select-all, and Shift-click range) ──
  const draftIds = useMemo(() => draft.map((d) => d._cid), [draft]);
  const allSelected = draft.length > 0 && sel.selected.size === draft.length;
  const someSelected = sel.selected.size > 0 && sel.selected.size < draft.length;
  const deleteSelected = () => {
    if (sel.selected.size === 0) return;
    setDraft((prev) => prev.filter((row) => !sel.selected.has(row._cid)));
    sel.clear();
    setDirty(true);
  };

  const addRow = () => {
    setDraft((prev) => {
      const next = [...prev, {
        _cid: nextCid(), original: null,
        timeS: '', frameLoadKN: '', axialDisp1Mm: '', axialDisp2Mm: '', radialStrainPct: '',
      }];
      setPage(Math.floor((next.length - 1) / rowsPerPage)); // jump to the new row
      return next;
    });
    setDirty(true);
  };

  const doSave = async (confirmReset?: boolean) => {
    if (draft.length === 0) { onNotify('Add at least one row before saving.', 'error'); return; }
    setSaving(true);
    try {
      const rows: RockTCSReadingEdit[] = draft.map((d) => ({
        timeS: toNum(d.timeS),
        frameLoadKN: toNum(d.frameLoadKN),
        axialDisp1Mm: toNum(d.axialDisp1Mm),
        axialDisp2Mm: toNum(d.axialDisp2Mm),
        radialStrainPct: toNum(d.radialStrainPct),
        machineDeviatorStressMPa: d.original?.machineDeviatorStressMPa ?? null,
        machineConfiningPressureMPa: d.original?.machineConfiningPressureMPa ?? null,
        machinePorePressureMPa: d.original?.machinePorePressureMPa ?? null,
        machineAxialStrainPct: d.original?.machineAxialStrainPct ?? null,
        machineVolStrainPct: d.original?.machineVolStrainPct ?? null,
      }));
      await saveRockTcsReadings(workOrderDetailKey, rows, confirmReset);
      onNotify('Test data saved — results recalculated.', 'success');
      setEditing(false);
      setDirty(false);
      setDraft([]);
      sel.clear();
      onSaved();
    } catch (err) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
      if (resp?.status === 409 && resp.data?.error === 'TEST_REQUIRES_RESET') {
        setConfirmDlg({
          open: true, title: 'Reset Required',
          message: `${resp.data.message ?? 'This test is in a protected status.'} Reset signoff and save?`,
          confirmLabel: 'Reset & Save', confirmColor: 'warning',
          onConfirm: () => { void doSave(true); },
        });
      } else {
        onNotify(resp?.data?.message || 'Failed to save test data', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── View mode ──
  if (!editing) {
    return (
      <Box>
        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small" variant="outlined" startIcon={<Edit sx={{ fontSize: 16 }} />}
              onClick={enterEdit} disabled={readings.length === 0}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Edit data
            </Button>
          </Box>
        )}
        <CombinedDataTable readings={readings} maxRowIndex={maxRowIndex} fiftyRowIndex={fiftyRowIndex} />
      </Box>
    );
  }

  // ── Edit mode ──
  const pageRows = draft.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
          Editing test data — {draft.length} row{draft.length !== 1 ? 's' : ''}.
          <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
            Only the raw machine inputs are editable; derived columns recalculate on save.
            {' '}{ROW_RANGE_SELECT_TIP}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addRow} disabled={saving}
            sx={{ textTransform: 'none' }}>
            Add row
          </Button>
          <Button
            size="small" color="error" startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
            onClick={deleteSelected} disabled={saving || sel.selected.size === 0}
            sx={{ textTransform: 'none' }}
          >
            Delete selected{sel.selected.size > 0 ? ` (${sel.selected.size})` : ''}
          </Button>
          <Button size="small" startIcon={<Close sx={{ fontSize: 16 }} />} onClick={cancelEdit} disabled={saving}
            sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />}
            onClick={() => void doSave()} disabled={saving || !dirty}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Save changes
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader sx={{ '& td, & th': { px: 0.75, py: 0.3, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700 }} />
                {groups.map((g) => (
                  <TableCell key={g.label} colSpan={g.span} align="center"
                    sx={{ fontSize: '0.62rem', fontWeight: 700, borderLeft: '1px solid', borderLeftColor: 'divider' }}>
                    {g.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700 }} />
              </TableRow>
              <TableRow>
                <TableCell padding="checkbox" sx={{ top: 25 }}>
                  <Tooltip title={SELECT_ALL_ROWS_TOOLTIP}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => sel.toggleSelectAll(draftIds)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ top: 25, fontSize: '0.62rem', fontWeight: 700 }}>#</TableCell>
                {TCS_TABLE_COLUMNS.map((c) => {
                  const reg = formulaFor(c.columnKey);
                  return (
                    <FormulaHeaderTooltip
                      key={c.field}
                      label={`${c.label}${c.unit ? ` (${c.unit})` : ''}`}
                      formula={reg?.formula}
                      note={reg?.note}
                    >
                      <TableCell align="right" sx={{ top: 25, fontSize: '0.62rem', fontWeight: 700 }}>
                        {c.label}{c.unit ? ` (${c.unit})` : ''}
                      </TableCell>
                    </FormulaHeaderTooltip>
                  );
                })}
                <TableCell align="center" sx={{ top: 25, fontSize: '0.62rem', fontWeight: 700 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row, i) => {
                const idx = page * rowsPerPage + i;
                return (
                  <TableRow key={row._cid} hover selected={sel.isSelected(row._cid)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" {...sel.getRowCheckboxProps(draftIds, idx)} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{idx + 1}</TableCell>
                    {TCS_TABLE_COLUMNS.map((c) => {
                      if (EDITABLE_FIELDS.has(c.field)) {
                        return (
                          <TableCell key={c.field} align="right" sx={{ p: 0.25 }}>
                            <input
                              value={row[c.field as keyof DraftRow] as string}
                              onChange={(e) => setCell(row._cid, c.field as keyof DraftRow, e.target.value)}
                              inputMode="decimal"
                              style={{
                                width: 72, textAlign: 'right', fontSize: '0.68rem', padding: '2px 4px',
                                border: '1px solid rgba(0,0,0,0.23)', borderRadius: 3, background: '#fff',
                              }}
                            />
                          </TableCell>
                        );
                      }
                      const v = row.original ? (row.original[c.field as keyof RockTCSReadingRow] as number | null) : null;
                      return (
                        <TableCell key={c.field} align="right" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                          {row.original ? fmt(v, c.decimals) : '—'}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ p: 0.25 }}>
                      <Tooltip title="Delete row">
                        <IconButton size="small" onClick={() => deleteRow(row._cid)} sx={{ p: 0.25, color: 'error.main' }}>
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={draft.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[50, 100, 250]}
        />
      </Paper>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
